import React from "react";
import { Box, Typography } from "@mui/material";

const imgSizes = {
  xs: "70px",
  sm: "80px",
  md: "90px",
  lg: "100px",
  xl: "120px",
};

const DefaultCircularDiagranItem = ({ item, transformValue, ...props }) => {
  return (
    <Box
      sx={{
        position: "absolute",
        transform: transformValue, // Position based on dynamic angle
        transformOrigin: "50% 50%",
        width: { xs: "25%", sm: "18%", md: "15%" },
        aspectRatio: "1 / 1",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          // Use the gradient as the background for the outer box
          background:
            "linear-gradient(180deg, #0596A1 0%, #3971F6 19.71%, #9A45F4 42.31%, #FC7D06 61.54%, #F7E144 80.77%, #FF4626 100%)",
          borderRadius: "50%",
          position: "relative",
          p: "2px", // This padding creates the visible border width
          width: imgSizes,
          height: imgSizes,
          // Center the content inside this box
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          boxShadow: "0px 0px 20px #9A45F4",
          transition: "transform .3s ease",
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      >
        {/* Inner Box: Contains the image and has the solid background */}
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: "50%",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src={item.image}
            alt={item.label}
            style={{
              objectFit: "cover",
              width: "100%",
              height: "100%",
            }}
          />
        </Box>
      </Box>
      <Typography
        variant="p"
        sx={{
          mt: 1,
          fontSize: "16px",
          lineHeight: 1.2,
          color: "white",
          fontWeight: 700,
        }}
      >
        {item.label}
      </Typography>
    </Box>
  );
};

const CircularDiagram = ({
  items,
  offset = 150,
  children,
  circularItemComponent,
  ...props
}) => {
  // Ensure we have items to render
  if (!items || items.length === 0) {
    return null;
  }

  // Calculate the angle for each item
  const angleStep = 360 / items.length;

  return (
    <Box
      className={"circular-diagram"}
      {...props}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        width: "100%",
        aspectRatio: "1 / 1",
        maxWidth: "850px",
        mx: "auto",
        ...props.sx,
      }}
    >
      {/* Main central circle */}
      <Box
        sx={{
          width: { xs: "70%" },
          aspectRatio: "1 / 1",
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {children}
      </Box>

      {/* Spoke items around the circle */}
      {items.map((item, index) => {
        const angle = angleStep * index - 90; // Distribute items evenly
        const transformValue = `rotate(${angle}deg) translate(${offset}%) rotate(${-angle}deg)`;

        const Component = circularItemComponent
          ? circularItemComponent
          : DefaultCircularDiagranItem;
        return (
          <Component
            key={"circular-diagram" + index}
            index={index}
            item={item}
            transformValue={transformValue}
          />
        );
      })}
    </Box>
  );
};

export default CircularDiagram;
